/**
 * Which assets' map symbols the user has hidden, per user and org. Shared by
 * the Assets tab and the Events tab asset lists, and kept across reloads.
 */
import { defaultStorage, type StorageLike } from './eventsSeenState';

type Listener = () => void;

const EMPTY: ReadonlySet<string> = new Set();
const listeners = new Set<Listener>();
const snapshotByKey = new Map<string, ReadonlySet<string>>();

export function hiddenAssetSymbolsStorageKey(
  userGuid: string,
  groupGuid: string
): string {
  return `pratus:assets:hiddenSymbols:${userGuid}:${groupGuid}`;
}

export function subscribeHiddenAssetSymbols(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stored ids, ignoring anything that is not a JSON array of strings. */
export function readHiddenAssetSymbols(
  key: string,
  storage: StorageLike | null = defaultStorage()
): ReadonlySet<string> {
  if (!key || !storage) return EMPTY;
  try {
    const parsed: unknown = JSON.parse(storage.getItem(key) ?? '[]');
    if (!Array.isArray(parsed)) return EMPTY;
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return EMPTY;
  }
}

/** Stable snapshot for `useSyncExternalStore`. */
export function getHiddenAssetSymbolsSnapshot(
  key: string
): ReadonlySet<string> {
  if (!key) return EMPTY;
  const cached = snapshotByKey.get(key);
  if (cached) return cached;
  const value = readHiddenAssetSymbols(key);
  snapshotByKey.set(key, value);
  return value;
}

/** Show or hide the given assets' symbols and notify subscribers. */
export function setAssetSymbolsVisible(
  key: string,
  ids: string[],
  visible: boolean,
  storage: StorageLike | null = defaultStorage()
): ReadonlySet<string> {
  if (!key) return EMPTY;
  const current =
    snapshotByKey.get(key) ?? readHiddenAssetSymbols(key, storage);
  const next = new Set(current);
  for (const id of ids) {
    if (!id) continue;
    if (visible) next.delete(id);
    else next.add(id);
  }
  try {
    storage?.setItem(key, JSON.stringify([...next]));
  } catch {
    // Storage full or blocked: keep the choice for this session.
  }
  snapshotByKey.set(key, next);
  listeners.forEach((listener) => listener());
  return next;
}

/** Test helper: forget cached snapshots. */
export function resetHiddenAssetSymbolsCache() {
  snapshotByKey.clear();
}
